import type { Permission, User, UserRole } from '../types/auth.types';

const VALID_ROLES: UserRole[] = [
  'admin',
  'editor',
  'viewer',
  'customer',
  'staff',
  'leader',
  'researcher',
  'operator',
  'user',
];

const ADMIN_PERMISSIONS: Permission[] = [
  'map:view',
  'map:edit',
  'dashboard:view',
  'dashboard:edit',
  'admin',
  'admin:users',
  'admin:roles',
  'admin:menus',
  'admin:catalog',
  'settings:view',
  'ops:health',
  'ops:audit',
  'ops:jobs',
  'ops:stations',
  'reports:view',
  'reports:create',
  'admin:system-config',
  'ops:alert-settings',
];

const ROLE_DEFAULT_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: ADMIN_PERMISSIONS,
  editor: [
    'map:view',
    'map:edit',
    'dashboard:view',
    'dashboard:edit',
    'reports:view',
    'reports:create',
  ],
  leader: [
    'map:view',
    'map:edit',
    'dashboard:view',
    'dashboard:edit',
    'reports:view',
    'reports:create',
  ],
  researcher: [
    'map:view',
    'map:edit',
    'dashboard:view',
    'dashboard:edit',
    'reports:view',
    'reports:create',
  ],
  viewer: ['map:view', 'dashboard:view'],
  customer: ['map:view', 'dashboard:view'],
  user: ['map:view', 'dashboard:view'],
  staff: [
    'map:view',
    'dashboard:view',
    'ops:health',
    'ops:audit',
    'ops:jobs',
    'ops:stations',
    'ops:alert-settings',
  ],
  operator: [
    'map:view',
    'dashboard:view',
    'ops:health',
    'ops:audit',
    'ops:jobs',
    'ops:stations',
    'ops:alert-settings',
  ],
};

export interface ApiUserDto {
  id: string;
  username: string;
  role?: string;
  role_slug?: string;
}

/**
 * Chuẩn hóa slug role (login, JWT, `/me`) sang `UserRole` FE.
 * Slug không khớp → `viewer` (an toàn tối thiểu).
 */
export function normalizeRole(slug: string): UserRole {
  const s = slug.trim().toLowerCase();
  if (VALID_ROLES.includes(s as UserRole)) return s as UserRole;
  if (s === 'analyst') return 'editor';
  return 'viewer';
}

/** Map payload `user` từ API đăng nhập sang model `User` của FE. */
export function mapApiUserToUser(dto: ApiUserDto): User {
  const rawSlug = dto.role ?? dto.role_slug ?? '';
  const role = normalizeRole(rawSlug);
  return {
    id: dto.id,
    email: `${dto.username}@ies.local`,
    name: dto.username,
    role,
    roleSlug: rawSlug || undefined,
    permissions: ROLE_DEFAULT_PERMISSIONS[role] ?? ROLE_DEFAULT_PERMISSIONS.viewer,
  };
}

/**
 * Trích object profile từ body JSON: envelope `{ data }`, hoặc `{ data: { user } }`, hoặc `{ user }`, hoặc flat.
 */
export function unwrapMeProfilePayload(raw: unknown): Record<string, unknown> | null {
  if (raw === null || raw === undefined) return null;
  let cur: unknown = raw;
  if (typeof cur === 'object' && cur !== null && 'data' in cur) {
    const d = (cur as Record<string, unknown>).data;
    if (d !== undefined) cur = d;
  }
  if (cur && typeof cur === 'object' && 'user' in cur) {
    const u = (cur as Record<string, unknown>).user;
    if (u && typeof u === 'object') return u as Record<string, unknown>;
  }
  if (cur && typeof cur === 'object') return cur as Record<string, unknown>;
  return null;
}

/**
 * Map profile GET/PATCH `/api/auth/me` (có `role_slug`, `display_name`, …) sang `User`.
 */
export function mapMeProfileToUser(dto: Record<string, unknown>): User | null {
  const id = dto.id;
  const username = dto.username;
  if (typeof id !== 'string' || typeof username !== 'string') return null;

  const rawSlug =
    (typeof dto.role_slug === 'string' && dto.role_slug) ||
    (typeof dto.role === 'string' && dto.role) ||
    '';
  const role = normalizeRole(rawSlug);

  const email = typeof dto.email === 'string' ? dto.email : `${username}@ies.local`;
  const name =
    (typeof dto.display_name === 'string' && dto.display_name.trim()) ||
    (typeof dto.full_name === 'string' && dto.full_name.trim()) ||
    username;
  const avatar = typeof dto.avatar_url === 'string' ? dto.avatar_url : undefined;

  return {
    id,
    email,
    name,
    avatar,
    role,
    roleSlug: rawSlug || undefined,
    permissions: ROLE_DEFAULT_PERMISSIONS[role] ?? ROLE_DEFAULT_PERMISSIONS.viewer,
  };
}
